import type { InclusionOption } from "app/pages/sales-intelligence/types/commonMaps";
import type { IRelativeDateRangeOptions } from "app/pages/sales-intelligence/types/range";

import type { DynamicFilterMetadata } from "./dynamic-filter-values";

export const dynamicFilterDescriptionWidgetMap = {
    combobox: "combobox",
} as const;

export type DynamicFilterDescriptionWidget =
    (typeof dynamicFilterDescriptionWidgetMap)[keyof typeof dynamicFilterDescriptionWidgetMap];

// Group-level rendering hint. `modal` opens the group's value-selection in a dialog;
// `none`/`combobox` (and absence) render the members inline. Distinct from the field-level widget above.
export const dynamicFilterGroupWidgetMap = {
    none: "none",
    combobox: "combobox",
    modal: "modal",
} as const;
export type DynamicFilterGroupWidget = (typeof dynamicFilterGroupWidgetMap)[keyof typeof dynamicFilterGroupWidgetMap];

export const dynamicFilterDataTypeMap = {
    enum: "enum",
    enumArray: "enumArray",
    date: "date",
    numberRange: "numberRange",
} as const;

export type DynamicFilterDataType = (typeof dynamicFilterDataTypeMap)[keyof typeof dynamicFilterDataTypeMap];

export type DynamicFilterDescriptionOption = {
    /* Option ID */
    readonly key: string;
    /* Option value to display */
    readonly value: string;
};

type BaseDynamicFilterFieldDescription<
    DataType extends DynamicFilterDataType = Exclude<DynamicFilterDataType, "date" | "numberRange">,
    Values = ReadonlyArray<DynamicFilterDescriptionOption>,
> = {
    readonly id: string;
    /* Field identifier, used as the DTO field key */
    readonly key: string;
    /* Field name to display */
    readonly name: string;
    /* View type to display */
    readonly widget: DynamicFilterDescriptionWidget;

    readonly dataType: DataType;

    readonly values: Values;

    /* Opaque backend hints, round-tripped untouched by the FE */
    readonly metadata?: DynamicFilterMetadata;
};

export type DynamicFilterFieldDescription =
    | BaseDynamicFilterFieldDescription
    | BaseDynamicFilterFieldDescription<"date", IRelativeDateRangeOptions>
    | BaseDynamicFilterFieldDescription<"numberRange", readonly number[]>;

export type DynamicFilterGroupDescription = {
    readonly id: string;
    /* Named-filter (group) title to display */
    readonly name: string;

    readonly fields: readonly DynamicFilterFieldDescription[];

    /* Optional group rendering hint (popup vs inline). Absence renders inline, as before. */
    readonly widget?: DynamicFilterGroupWidget;

    /* Optional include/exclude options available for the group. Absence means no inclusion control. */
    readonly inclusions?: readonly InclusionOption[];
};
